import React, {
  useState,
  useMemo,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useRef,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'

import styled from 'styled-components'
import Joi from 'joi'
import toast from 'react-hot-toast'
import { RiPencilFill } from 'react-icons/ri'
import { FaTrashCan } from 'react-icons/fa6'
import moment from 'moment'
import { v4 as uuidv4 } from 'uuid'

import P from '../../../components/typography/P'
import { Button } from '../../../components/Button'
import { StandardInput, DatepickerInput } from '../../../components/Input'
import { SingleSelect } from '../../../components/Select'
import RadioButtonGroup from '../../../components/RadioButtonGroup'
import EmptyWrapper from '../../../components/EmptyWrapper'

import { getFromLocalStorage, parseJSON, capitalize } from '../../../utils/util'

import { setEducationalBackground } from '../../../redux/feature/applicantSlice'

import apiClient from '../../../helpers/apiClient'

const EducationalBackgrounds = forwardRef((props, ref) => {
  const dispatch = useDispatch()

  const { educationalBackground } = useSelector((state) => state.applicant)

  const validationSchema = useMemo(() => {
    return Joi.object({
      educationLevel: Joi.object({
        value: Joi.alternatives().try(Joi.string(), Joi.number()).required(),
        label: Joi.string().required(),
      })
        .label('Education Level')
        .required()
        .messages({
          'object.base': 'Education Level must be a valid selection.',
          'any.required': 'Education Level is required.',
        }),
      school: Joi.string().label('School').required(),
      schoolLocation: Joi.string().label('School Location').required(),
      currentStatus: Joi.string().label('Current Status').required(),
      fieldOfStudy: Joi.string().label('Field of Study').required(),
      from: Joi.date().label('Date From').required().messages({
        'date.base': '"Date From" must be a valid date',
        'any.required': '"Date From" is required',
      }),
      to: Joi.date()
        .label('Date To')
        .required()
        .custom((value, helpers) => {
          const from = helpers.state.ancestors[0].from
          if (!from) return value

          const fromDate = new Date(from)
          const toDate = new Date(value)

          const diffInDays = (toDate - fromDate) / (1000 * 60 * 60 * 24)

          if (diffInDays < 1) {
            return helpers.error('date.minDays')
          }

          return value
        })
        .messages({
          'date.base': '"Date To" must be a valid date',
          'any.required': '"Date To" is required',
          'date.minDays': '"Date To" must be at least 1 day after "Date From"',
        }),
    })
  }, [])

  const formRef = useRef(null)

  const [fieldErrors, setFieldErrors] = useState({})
  const [showForm, setShowForm] = useState(false)

  const [educationLevelList, setEducationLevelList] = useState([])

  const [id, setId] = useState(uuidv4())
  const [educationLevel, setEducationLevel] = useState({})
  const [fieldOfStudy, setFieldOfStudy] = useState('')
  const [school, setSchool] = useState('')
  const [schoolLocation, setSchoolLocation] = useState('')
  const [currentStatus, setCurrentStatus] = useState('graduated')
  const [from, setFrom] = useState(new Date().toISOString().split('T')[0])
  const [to, setTo] = useState(new Date().toISOString().split('T')[0])

  const [isEdit, setIsEdit] = useState(false)

  const applicantCVData = useMemo(
    () => getFromLocalStorage('applicant-cv-data'),
    []
  )

  const resetFields = () => {
    setEducationLevel(educationLevelList[0])
    setFieldOfStudy('')
    setSchool('')
    setSchoolLocation('')
    setCurrentStatus('graduated')
    setFrom(new Date().toISOString().split('T')[0])
    setTo(new Date().toISOString().split('T')[0])
  }

  /**
   * @returns {boolean} Whether the form data is valid.
   */
  const handleNext = () => {
    const meta = parseJSON(applicantCVData?.meta, {})

    const updatedMeta = {
      ...meta,
      educationalData: educationalBackground,
    }

    const updatedApplicantCVData = {
      ...applicantCVData,
      meta: JSON.stringify(updatedMeta),
    }

    // save to local storage...
    localStorage.setItem(
      'applicant-cv-data',
      JSON.stringify(updatedApplicantCVData)
    )

    return true
  }

  const handleSelectEducationLevel = (e) => setEducationLevel(e)

  const handleShowForm = () => {
    setFieldErrors({})
    setIsEdit(false)
    setShowForm(true)
  }

  const handleCloseForm = () => {
    setShowForm(false)
    resetFields()
  }

  const handleSaveEducationalBackground = () => {
    const payload = {
      educationLevel,
      fieldOfStudy,
      school,
      schoolLocation,
      currentStatus,
      from,
      to,
    }

    // Validate form data against schema, collecting all errors
    const result = validationSchema.validate(payload, { abortEarly: false })

    // If there are validation errors, set error messages for fields and notify the user
    if (result.error) {
      const validationErrors = result.error.details.reduce((acc, curr) => {
        acc[curr.context.key] = curr.message.replace(/"/g, '')
        return acc
      }, {})

      setFieldErrors(validationErrors)
      toast.error('Please fix the validation errors to continue.')

      return
    }

    payload.educationLevel = educationLevel.label

    let newEducationalBackgrounds = []

    if (isEdit) {
      payload.id = id
      newEducationalBackgrounds = educationalBackground.map((e) =>
        String(e.id) === String(id) ? payload : e
      )
    } else {
      payload.id = uuidv4()
      newEducationalBackgrounds = [...educationalBackground, payload]
    }

    dispatch(setEducationalBackground(newEducationalBackgrounds))

    setShowForm(false)
    setIsEdit(false)
    resetFields()
  }

  const handleEditEducationalBackground = (data) => {
    setIsEdit(true)

    setId(data?.id)

    const selectedEducationLevel = educationLevelList?.filter(
      (e) => e.label === data?.educationLevel
    )[0]
    setEducationLevel(selectedEducationLevel)

    setFieldOfStudy(data?.fieldOfStudy)
    setSchool(data?.school)
    setSchoolLocation(data?.schoolLocation)
    setCurrentStatus(data?.currentStatus.toLowerCase())
    setFrom(data?.from)
    setTo(data?.to)

    setShowForm(true)
  }

  const handleDeleteEducationalBackground = (data) => {
    const removedEducationalBackground = educationalBackground.filter(
      (e) => e.id !== data.id
    )

    dispatch(setEducationalBackground(removedEducationalBackground))

    setShowForm(false)
  }

  useEffect(() => {
    apiClient.get(`/education-levels`).then((response) => {
      const data = response?.data?.data || []
      const formattedEducationalLevels = data
        .filter((level) => level.name !== 'No Formal Education')
        .map((level) => ({
          value: level.id,
          label: level.name,
        }))
      setEducationLevelList(formattedEducationalLevels)
      setEducationLevel(formattedEducationalLevels[0])
    })

    if (applicantCVData) {
      const meta = parseJSON(applicantCVData?.meta, {})
      dispatch(setEducationalBackground(meta?.educationalData || []))
    }
  }, [])

  useEffect(() => {
    // automatically scroll to the form
    if (showForm && formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [showForm])

  useImperativeHandle(ref, () => ({
    onNext: handleNext,
  }))

  return (
    <StyledEducationalBackgrounds>
      <P size="1.5rem" weight="500" mb="0.25rem">
        Educational Backgrounds
      </P>

      <P color="#6F6F6F" mb="1rem">
        List your academic qualifications to showcase your educational
        background.
      </P>
      <Button handleClick={handleShowForm} width="50%" btnClass="primary">
        Add new educational background
      </Button>
      <Container>
        {educationalBackground?.length > 0 ? (
          educationalBackground?.map((e, i) => (
            <ListItem key={i}>
              <ListItemHeader>
                <P weight="500">
                  {e.educationLevel}, {e.fieldOfStudy}
                </P>

                <ListItemIconsWrapper>
                  <RiPencilFill
                    className="icon"
                    onClick={() => handleEditEducationalBackground(e)}
                  />
                  <FaTrashCan
                    className="icon"
                    onClick={() => handleDeleteEducationalBackground(e)}
                  />
                </ListItemIconsWrapper>
              </ListItemHeader>
              <ListItemBody>
                <P>
                  {e.school}, {e.schoolLocation}
                </P>
                <ListItemFooter>
                  <P>
                    {moment(e.from).format('LL')} - {moment(e.to).format('LL')}
                  </P>
                  <P weight="500">{capitalize(e.currentStatus)}</P>
                </ListItemFooter>
              </ListItemBody>
            </ListItem>
          ))
        ) : (
          <EmptyWrapper maxHeight="100%" marginBottom="1rem" marginTop="1rem">
            You have no educational background.
          </EmptyWrapper>
        )}

        {showForm && (
          <FormContainer ref={formRef}>
            <FormGroup>
              <SingleSelect
                title="Level of Education"
                options={educationLevelList}
                selected={educationLevel}
                placeholder="Select Education Level"
                onChange={handleSelectEducationLevel}
                wrapperClass={fieldErrors.country && 'error'}
                subText={fieldErrors.country}
              />
              <StandardInput
                title="Field of Study"
                type="text"
                value={fieldOfStudy}
                onChange={(value) => setFieldOfStudy(value)}
                required
                wrapperClass={fieldErrors.fieldOfStudy && 'error'}
                subText={fieldErrors.fieldOfStudy}
                placeholder="Enter Field of Study"
                marginBottom="0.5rem"
              />
            </FormGroup>
            <FormGroup>
              <StandardInput
                title="School"
                type="text"
                value={school}
                onChange={(value) => setSchool(value)}
                required
                wrapperClass={fieldErrors.school && 'error'}
                subText={fieldErrors.school}
                placeholder="Enter School"
                marginBottom="0.5rem"
              />
              <StandardInput
                title="School Location"
                type="text"
                value={schoolLocation}
                onChange={(value) => setSchoolLocation(value)}
                required
                wrapperClass={fieldErrors.schoolLocation && 'error'}
                subText={fieldErrors.schoolLocation}
                placeholder="Enter School Location"
                marginBottom="0.5rem"
              />
            </FormGroup>
            <FormGroup>
              <RadioButtonGroup
                title="Current Status"
                list={[
                  {
                    value: 'graduated',
                    label: 'Graduated',
                  },
                  {
                    value: 'currently enrolled',
                    label: 'Currently Enrolled',
                  },
                ]}
                selected={currentStatus}
                onChange={setCurrentStatus}
              />
            </FormGroup>
            <FormGroup>
              <DatepickerInput
                title="Date From"
                value={from}
                onChange={(date) => setFrom(date)}
                dateFormat="MMMM dd, yyyy"
                placeholderText="MM/YYYY"
              />
              <DatepickerInput
                title="Date To"
                value={to}
                onChange={(date) => setTo(date)}
                dateFormat="MMMM dd, yyyy"
                placeholderText="MM/YYYY"
                wrapperClass={fieldErrors.to && 'error'}
                subText={fieldErrors.to}
              />
            </FormGroup>
            <ButtonWrapper>
              <Button
                handleClick={handleSaveEducationalBackground}
                btnClass="success"
              >
                Save Record
              </Button>
              <Button handleClick={handleCloseForm} btnClass="primary">
                Close Form
              </Button>
            </ButtonWrapper>
          </FormContainer>
        )}
      </Container>
    </StyledEducationalBackgrounds>
  )
})

const StyledEducationalBackgrounds = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: 1rem;
`

const FormGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  width: 100%;
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
`

const ListItem = styled.div`
  width: 100%;
  background: rgb(248, 249, 255);
  padding: 0.5rem;
  border-radius: 3px;
  position: relative;
  margin-top: 1rem;
  font-weight: 300;
`

const ListItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
`

const ListItemIconsWrapper = styled.div`
  .icon {
    font-size: 0.875rem;
    color: rgb(174, 179, 201);
    margin-left: 1rem;
    cursor: pointer;
  }

  .icon:hover {
    color: rgba(47, 52, 81, 1);
  }
`

const ListItemBody = styled.div`
  display: flex;
  flex-direction: column;
`

const ListItemFooter = styled.div`
  display: flex;
  justify-content: space-between;
`

const FormContainer = styled.div`
  padding: 0.5rem;
  margin-top: 1rem;
`

const ButtonWrapper = styled.div`
  display: flex;
  gap: 1rem;
`

EducationalBackgrounds.displayName = 'EducationalBackgrounds'

export default EducationalBackgrounds
